import React from 'react';
import {
    alpha,
    Button,
    ClickAwayListener,
    Grow,
    IconButton,
    MenuList,
    Paper,
    Popper,
    Stack,
    Tooltip,
} from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { SxProps } from '@mui/system';
import { useHierarchyMenu } from './HierarchyMenuContext';

export default function ControlledDropdownIcon({
    children,
    icon,
    label,
    isButtonView,
    tooltip,
    menuSx,
    popperSx,
    iconButtonProps,
    iconSize = '16px',
    placement,
    onChangeState = () => {},
    onClickInside,
    iconFirst = false,
    disabled = false,
    menuId,
}: {
    children: any;
    icon: string;
    label?: string;
    isButtonView?: boolean;
    tooltip?: string;
    menuSx?: SxProps;
    popperSx?: SxProps;
    iconButtonProps?: SxProps;
    placement?: any;
    iconSize?: string;
    iconFirst?: boolean;
    onChangeState?: (isOpened: boolean) => void;
    onClickInside?: (event: any, handleClose: (event: any) => void) => void;
    disabled?: boolean;
    menuId: string;
}) {
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const { openMenuId, setOpenMenuId } = useHierarchyMenu();

    const open = openMenuId === menuId;

    const handleClick = (event: any) => {
        const newState = !open;
        setOpenMenuId(newState ? menuId : null);
        onChangeState(newState);
    };

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpenMenuId(null);
        onChangeState(false);
    };

    return (
        <>
            <Tooltip title={tooltip ?? ''}>
                {isButtonView ? (
                    <Button
                        ref={anchorRef}
                        variant="outlined"
                        color="neutral"
                        endIcon={<IconMi icon={icon} sx={{ fontSize: iconSize }} />}
                        aria-controls={open ? 'composition-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                        className={open ? 'active-state' : undefined}
                        disabled={disabled}
                    >
                        {label ? label : ''}
                    </Button>
                ) : (
                    <IconButton
                        ref={anchorRef}
                        sx={iconButtonProps ? iconButtonProps : { p: '6px', fontSize: 12 }}
                        aria-controls={open ? 'composition-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                        className={open ? 'active-state' : undefined}
                        disabled={disabled}
                    >
                        {iconFirst ? (
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <IconMi icon={icon} sx={{ fontSize: iconSize }} />
                                {label ? label : ''}
                            </Stack>
                        ) : (
                            <>
                                {label ? label : ''}
                                <IconMi icon={icon} sx={{ fontSize: iconSize }} />
                            </>
                        )}
                    </IconButton>
                )}
            </Tooltip>

            {open && (
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement={placement ?? 'bottom-start'}
                    transition
                    sx={{
                        ...{
                            zIndex: 1491,
                        },
                        ...(popperSx ?? {}),
                    }}
                >
                    {({ TransitionProps }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: 'left top',
                            }}
                        >
                            <Paper
                                sx={{
                                    borderRadius: '4px',
                                    boxShadow: (theme) => `0px 4px 16px 0px ${alpha(theme.palette.text.primary, 0.16)}`,
                                    overflow: 'auto',
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList
                                        variant={'menu'}
                                        sx={{ ...{ py: 0 }, ...(menuSx ?? {}) }}
                                        autoFocusItem={open}
                                        id="composition-menu"
                                        aria-labelledby="composition-button"
                                        onClick={
                                            onClickInside ? (event) => onClickInside(event, handleClose) : handleClose
                                        }
                                    >
                                        {children}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            )}
        </>
    );
}
